import React, { useCallback, useEffect, useMemo } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent
} from 'shared/components/common/modal'
import { useModifierContext } from 'public/components/online_ordering/ModifierContext'
import { t } from 'src/translations'
import { ModifierGroup } from 'public/components/online_ordering/types'
import SelectionList from 'public/components/default_template/online_ordering/item_modal/SelectionList'
import { ArrowBackIcon } from '@toasttab/buffet-pui-icons'
import classnames from 'classnames'

export const ProductDisplayNestedModifierModal = () => {
  const {
    displayedModifier,
    modifiers,
    dropModifier,
    deselect,
    commitModifier,
    validate,
    lastAction,
    returnToRoot
  } = useModifierContext()

  const isNestedMod = useMemo(() => modifiers.length > 2, [modifiers])

  useEffect(() => {
    if (isNestedMod && lastAction === 'push') {
      const element = document.getElementById('content-body')
      element?.classList.add('animateEnter')
      setTimeout(() => {
        element?.classList.remove('animateEnter')
      }, 300) // This timeout needs to match the animation durations defined in item_modal.scss
    }
  }, [isNestedMod, displayedModifier, lastAction])

  const runAnimation = useCallback(
    (cb: () => void, dismissAllContent?: boolean) => {
      const animationClassName =
        isNestedMod && !dismissAllContent ? 'animateExit' : 'slideDown'
      const elementId =
        isNestedMod && !dismissAllContent ? 'content-body' : 'secondary-content'
      const element = document.getElementById(elementId)
      element?.classList.add(animationClassName)
      setTimeout(() => {
        element?.classList.remove(animationClassName)
        cb()
      }, 300) // This timeout needs to match the animation durations defined in item_modal.scss
    },
    [isNestedMod]
  )

  const submitModifier = useCallback(() => {
    if (displayedModifier?.isRoot || !validate()) {
      return
    }
    runAnimation(commitModifier)
  }, [displayedModifier?.isRoot, validate, runAnimation, commitModifier])

  const animatedDropModifier = useCallback(
    (forceAnimate?: boolean) => {
      // Order is important here: first we disregard any current selections, then we
      // remove the modifier from the parent modifier group
      if (lastAction === 'pop' || forceAnimate) {
        runAnimation(dropModifier)
      } else {
        dropModifier()
      }
      deselect(
        displayedModifier!.groupGuid,
        displayedModifier!.modifier.itemGuid
      )
    },
    [lastAction, deselect, displayedModifier, runAnimation, dropModifier]
  )

  const previousModifier = useMemo(
    () => (modifiers.length > 2 ? modifiers[modifiers.length - 2] : undefined),
    [modifiers]
  )

  if (!displayedModifier) return null

  const isSelected =
    modifiers.length > 1 &&
    displayedModifier?.groupGuid &&
    !!modifiers[modifiers.length - 2]?.modifierGroups[
      displayedModifier?.groupGuid
    ]?.[displayedModifier.modifier.itemGuid]

  return (
    <Modal isOpen={true} onClose={returnToRoot}>
      <ModalOverlay opacity={65} fadeIn fadeOut duration={0.3} />
      <ModalContent
        wrapperClassName='itemModalWrapper'
        contentClassName='itemModalContent modalContentV2 fullScreenMobile'
      >
        <div className='itemModalContainer'>
          <div className='itemModal' style={{ minHeight: '80vh' }}>
            <div
              id='secondary-content'
              className={classnames('secondaryContent', {
                slideUp: !isNestedMod && lastAction !== 'pop'
              })}
            >
              {isNestedMod && (
                <button
                  className='itemModalBackButton'
                  onClick={() => animatedDropModifier(true)}
                >
                  <ArrowBackIcon size='md' aria-label='Back button' />
                </button>
              )}
              <button
                className='itemModalCancelButton'
                onClick={() => runAnimation(returnToRoot, true)}
              >
                {t('item-modal.cancel-button')}
              </button>
              <div className='pinnedHeader pinned'>
                {displayedModifier.modifier.name}
              </div>
              {previousModifier && (
                <div className='contentBody prev'>
                  <div className='modSections' role='list'>
                    {previousModifier.modifier.modifierGroups.map(
                      (group: ModifierGroup, index) => (
                        <SelectionList
                          key={group.guid}
                          group={group}
                          firstModifierGroup={index === 0}
                        />
                      )
                    )}
                  </div>
                </div>
              )}
              <div id='content-body' className='contentBody'>
                <div className='modSections' role='list'>
                  {displayedModifier.modifier.modifierGroups.map(
                    (group: ModifierGroup, index) => (
                      <SelectionList
                        key={group.guid}
                        group={group}
                        firstModifierGroup={index === 0}
                      />
                    )
                  )}
                </div>
              </div>
              <div className='actionWrapper'>
                <button
                  type='button'
                  className='modalButton secondary'
                  onClick={submitModifier}
                  data-testid='modifier-item-continue-cta'
                >
                  {t('item-modal.confirm-selection')}
                </button>
                {isSelected && (
                  <button
                    type='button'
                    className='modalButton secondary remove'
                    onClick={() => animatedDropModifier(false)}
                  >
                    {t('item-modal.remove')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </ModalContent>
    </Modal>
  )
}
